import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import {
  addToItems,
  changeQty,
  db,
  findByBarcode,
  openOrderForTable,
  saveItems,
  topProductsToday,
  type OrderItem,
  type PayMethod,
  type Product,
} from '../lib/db'
import { useBarcodeScanner } from '../lib/scanner'
import { money, qtyToText, QTY_UNIT } from '../lib/money'
import { useApp } from '../lib/store'
import { DiscountSheet } from '../components/DiscountSheet'
import { WeightSheet } from '../components/WeightSheet'
import { PageBack } from '../components/PageBack'
import { Thumb } from '../components/Thumb'

/**
 * Bán nhanh tại quầy, bộ Modern.
 *
 * Hai khoang: lưới món bên trái, giỏ hàng bên phải. Bán tại quầy là gõ liên tục — khách
 * đứng đợi ngay trước mặt — nên món và giỏ phải cùng nằm trên một màn hình, không có
 * bước chuyển màn hình nào ở giữa.
 *
 * Đơn của màn hình này gắn vào ô "Mang về" như bộ Classic vẫn làm, chứ không đẻ ra một
 * khái niệm đơn mới: đơn mang về hiện luôn trên sơ đồ bàn, và mọi thứ đã dựng quanh đơn
 * hàng — đồng bộ, hoá đơn, ca làm — chạy tiếp không phải sửa gì.
 */
export function QuickSellScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)

  const [orderId, setOrderId] = useState<string | null>(null)
  const [catId, setCatId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [method, setMethod] = useState<PayMethod>('Cash')
  const [weighing, setWeighing] = useState<Product | null>(null)
  const [discounting, setDiscounting] = useState(false)

  const categories = useLiveQuery(() => db.categories.orderBy('sortOrder').toArray(), [], [])
  const products = useLiveQuery(() => db.products.orderBy('sortOrder').toArray(), [], [])
  const order = useLiveQuery(() => (orderId ? db.orders.get(orderId) : undefined), [orderId])
  const top = useLiveQuery(() => topProductsToday(3), [], [])

  // Đơn mang về đang mở thì dùng tiếp, chưa có thì mở một cái. Giống hệt nút Bán nhanh
  // của bộ Classic, nên hai bộ không bao giờ đẻ ra hai đơn song song cho cùng một quầy.
  useEffect(() => {
    let alive = true
    void (async () => {
      const takeaway =
        (await db.diningTables.filter((t) => t.isTakeaway).first()) ??
        (await db.diningTables.toCollection().first())
      if (!takeaway) {
        say('Quán chưa có bàn nào — vào Sơ đồ bàn thêm một ô Mang về')
        return
      }
      const o = await openOrderForTable(takeaway)
      if (alive) setOrderId(o.id)
    })()
    return () => {
      alive = false
    }
  }, [say])

  const hotNames = useMemo(() => new Set(top.map((t) => t.name)), [top])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return products.filter((p) => {
      if (q) return p.name.toLowerCase().includes(q) || (p.barcode ?? '').includes(q)
      return catId === null || p.categoryId === catId
    })
  }, [products, catId, query])

  const items = order?.items ?? []

  // Dòng trong giỏ chỉ giữ tên và giá lúc bán, không giữ mã ảnh — tra ngược ra sản phẩm.
  const imageOf = (productId: string | null) =>
    productId ? (products.find((x) => x.id === productId)?.imageKey ?? null) : null

  async function add(p: Product, option: string | null) {
    if (!order) return
    if (p.soldByWeight) {
      setWeighing(p)
      return
    }
    await saveItems(order.id, addToItems(items, p, option))
  }

  // Máy scan quét mã là thêm thẳng vào giỏ. Tắt khi đang mở bảng cân hoặc bảng giảm giá —
  // lúc đó người dùng đang gõ số, không phải đang quét hàng.
  useBarcodeScanner({
    enabled: !weighing && !discounting,
    onScan: async (code) => {
      const found = await findByBarcode(code)
      if (!found) {
        say(`Chưa gán mã ${code} cho món nào`)
        return
      }
      await add(found, null)
      say(`+ ${found.name}`)
    },
  })

  async function bump(item: OrderItem, delta: number) {
    if (!order) return
    await saveItems(order.id, changeQty(items, item.id, delta * QTY_UNIT))
  }

  async function drop(itemId: string) {
    if (!order) return
    await saveItems(
      order.id,
      items.filter((i) => i.id !== itemId),
    )
  }

  async function clearCart() {
    if (!order) return
    await saveItems(order.id, [])
    say('Đã xoá giỏ hàng')
  }

  if (!order) return <div className="screen" />

  return (
    <div className="screen">
      <div className="board quick-board">
        <header className="page-head">
          <PageBack to={'/'} />
          <span className="page-icon is-amber" aria-hidden="true">
            ⚡
          </span>
          <div className="page-title">
            <h1>Bán nhanh</h1>
            <p>Tạo đơn và thu tiền ngay tại quầy</p>
          </div>

          <label className="search search-wide">
            <span className="sr-only">Tìm món</span>
            <span className="search-glyph" aria-hidden="true">
              ⌕
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tìm món, hoặc quét mã vạch…"
              inputMode="search"
            />
            <span className="search-scan" aria-hidden="true" title="Quét mã vạch được ngay, không cần bấm vào đây">
              ▮▮▮
            </span>
          </label>
        </header>

        <div className="quick-split">
          <div className="quick-main">
            <div className="chip-row zone-row">
              <button
                type="button"
                className="chip"
                aria-pressed={catId === null && !query}
                onClick={() => {
                  setCatId(null)
                  setQuery('')
                }}
              >
                Tất cả
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="chip"
                  aria-pressed={catId === c.id && !query}
                  onClick={() => {
                    setCatId(c.id)
                    setQuery('')
                  }}
                >
                  {c.name}
                </button>
              ))}
            </div>

            {shown.length === 0 ? (
              <div className="empty">
                <div className="empty-icon" aria-hidden="true">
                  🔍
                </div>
                <p>{query ? `Không có món nào khớp "${query}".` : 'Nhóm này chưa có món nào.'}</p>
              </div>
            ) : (
              <div className="quick-grid">
                {shown.map((p) => {
                  const inCart = items
                    .filter((i) => i.productId === p.id)
                    .reduce((s, i) => s + i.qtyMilli, 0)

                  return (
                    <div key={p.id} className="quick-card">
                      {hotNames.has(p.name) && (
                        <span className="quick-hot" title="Bán chạy hôm nay">
                          <span aria-hidden="true">🔥</span> Bán chạy
                        </span>
                      )}
                      {inCart > 0 && <span className="quick-count num">{qtyToText(inCart)}</span>}

                      <button
                        type="button"
                        className="quick-face"
                        onClick={() => void add(p, null)}
                        aria-label={`Thêm ${p.name}, ${money(p.price)} đồng`}
                      >
                        <Thumb name={p.name} imageKey={p.imageKey} className="quick-thumb" />
                        <b className="quick-name">{p.name}</b>
                        <span className="quick-price num">{money(p.price)}đ</span>
                        <span className="quick-add" aria-hidden="true">
                          +
                        </span>
                      </button>

                      {p.quickOptions.length > 0 && (
                        <div className="product-opts">
                          {p.quickOptions.map((o) => (
                            <button key={o} type="button" onClick={() => void add(p, o)}>
                              {o}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <aside className="cart-pane" aria-label="Giỏ hàng">
            <div className="pane-head">
              <span className="pane-name">🧾 Đơn hàng</span>
              <span className="pane-seats">{order.code}</span>
              {items.length > 0 && (
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => void clearCart()}
                  aria-label="Xoá giỏ hàng"
                  title="Xoá giỏ hàng"
                >
                  🗑
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <div className="pane-empty">
                <div className="empty-icon" aria-hidden="true">
                  🛒
                </div>
                <p>Bấm vào món bên trái, hoặc quét mã vạch để thêm vào đơn.</p>
              </div>
            ) : (
              <>
                <div className="pane-lines">
                  {items.map((i) => (
                    <div key={i.id} className="pane-line">
                      <Thumb name={i.name} imageKey={imageOf(i.productId)} className="pane-thumb" />

                      <div className="pane-line-main">
                        <b>
                          {i.name}
                          {i.optionLabel && <em> · {i.optionLabel}</em>}
                        </b>
                        <div className="stepper">
                          <button type="button" onClick={() => void bump(i, -1)} aria-label={`Bớt ${i.name}`}>
                            −
                          </button>
                          <span className="num">{qtyToText(i.qtyMilli)}</span>
                          <button type="button" onClick={() => void bump(i, 1)} aria-label={`Thêm ${i.name}`}>
                            +
                          </button>
                        </div>
                      </div>

                      <span className="pane-line-total num">{money(i.lineTotal)}</span>

                      <button
                        type="button"
                        className="pane-drop"
                        onClick={() => void drop(i.id)}
                        aria-label={`Bỏ ${i.name} khỏi đơn`}
                        title="Bỏ món"
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </div>

                <div className="cart-sums">
                  <div className="pay-row">
                    <span>Tạm tính</span>
                    <b className="num">{money(order.subtotal)}</b>
                  </div>
                  <button type="button" className="pay-row is-link" onClick={() => setDiscounting(true)}>
                    <span>{order.discount > 0 ? (order.discountLabel ?? 'Giảm giá') : 'Giảm giá'}</span>
                    {order.discount > 0 ? (
                      <b className="num">−{money(order.discount)}</b>
                    ) : (
                      <b>Bấm để bớt</b>
                    )}
                  </button>
                  <div className="pay-row big divider">
                    <span>Tổng cộng</span>
                    <b className="num">{money(order.total)}đ</b>
                  </div>
                </div>

                <div className="cart-pay">
                  <div className="seg">
                    <button
                      type="button"
                      aria-pressed={method === 'Cash'}
                      onClick={() => setMethod('Cash')}
                    >
                      💵 Tiền mặt
                    </button>
                    <button
                      type="button"
                      aria-pressed={method === 'Transfer'}
                      onClick={() => setMethod('Transfer')}
                    >
                      📱 Chuyển khoản
                    </button>
                  </div>

                  <button
                    type="button"
                    className="btn btn-brand btn-lg wide"
                    onClick={() => nav(`/pay/${order.id}?m=${method}`)}
                  >
                    Thu tiền · {money(order.total)}đ
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
      </div>

      {discounting && <DiscountSheet order={order} onClose={() => setDiscounting(false)} />}

      {weighing && (
        <WeightSheet
          product={weighing}
          onClose={() => setWeighing(null)}
          onConfirm={async (qtyMilli) => {
            const next: OrderItem[] = [
              ...items,
              {
                id: crypto.randomUUID(),
                productId: weighing.id,
                name: weighing.name,
                unitPrice: weighing.price,
                qtyMilli,
                lineTotal: Math.round((weighing.price * qtyMilli) / QTY_UNIT),
                optionLabel: null,
                note: null,
              },
            ]
            await saveItems(order.id, next)
            setWeighing(null)
          }}
        />
      )}
    </div>
  )
}
